import { Box } from "@mui/material";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { JSX } from "react";
import { useSearchParams } from "react-router-dom";

import {
  URGENCY_SUPER_HOT_DAYS,
  type SalesLeadSource,
  type SalesLeadsListPayload,
  type SalesVertical,
} from "@xop/dashboard-shared";

import { PageLoader } from "@/components/ui/page-loader";

import { salesApi, type ListLeadsParams } from "../client.js";
import { LeadsTable } from "../components/LeadsTable.js";
import { SalesEmptyState, SalesErrorState } from "../components/SalesEmptyState.js";
import { devLog } from "../devLog.js";
import { LeadFilters } from "./LeadFilters.js";
import {
  DEFAULT_LEAD_SORT,
  leadSortParams,
  sameSort,
  type LeadSort,
} from "./leadSort.js";
import { LeadsPagination } from "./leads-list/LeadsPagination.js";
import { StatusFilter } from "./leads-list/StatusFilter.js";
import { STATUS_FILTERS, filterParams, normalizeFilterKey } from "./leads-list/statusFilters.js";

interface Filters {
  q: string;
  vertical: SalesVertical | undefined;
  country: string;
  /** Which bot found the lead — `discovery` (Lead Generator) / `harvester`. */
  source: SalesLeadSource | undefined;
  /** SERVER-side ordering, as the Leads-table `{ key, dir }`. Every clickable
   * column maps to a server `sort` so a header click (or the "Sort" dropdown)
   * orders the WHOLE dataset, not just the visible page. See `leadSort.ts`. */
  sort: LeadSort;
}

const PER_PAGE = 50;

/**
 * The Leads list — full org-centric leads table with a status filter, the
 * search/country/vertical filter row, and bottom-mounted pagination. Clicking a
 * row's org name navigates to `/sales/leads/:id` (the full-page detail).
 *
 * This file is the data orchestrator: the list query + the per-filter count
 * queries + the filter/page state. The status pills live in
 * `leads-list/StatusFilter`, the pager in `LeadsPagination`, and the filter
 * key/param logic in `leads-list/statusFilters`.
 */
export function LeadsPage(): JSX.Element {
  useEffect(() => devLog("leads/mount"), []);

  const [filters, setFilters] = useState<Filters>({
    q: "",
    vertical: undefined,
    country: "",
    source: undefined,
    sort: DEFAULT_LEAD_SORT,
  });
  const [page, setPage] = useState(1);
  // "All" is the default/primary filter — the operator lands on every lead.
  // A `?tab=` deep link (the dashboard pulse's "Closing soon" card, a CRM
  // `?tab=qualified` cross-link, or `/sales/today` → `?tab=today`) opens
  // straight to that filter.
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<string>(() =>
    normalizeFilterKey(searchParams.get("tab")),
  );
  const activeFilter = STATUS_FILTERS.find((t) => t.key === tab);

  // Reset to page 1 whenever the filter set changes so the operator
  // doesn't get stranded on a now-empty page 4 after narrowing.
  const setFiltersAndResetPage = (patch: Partial<Filters>) => {
    setFilters((cur) => ({ ...cur, ...patch }));
    setPage(1);
  };

  const leads = useQuery({
    queryKey: ["sales", "leads", tab, filters, page],
    queryFn: () => {
      // exactOptionalPropertyTypes forbids passing `undefined` to an
      // optional field — build the params object via conditional
      // spreads so an empty filter just omits the key.
      const params: ListLeadsParams = {
        per_page: PER_PAGE,
        page,
        ...filterParams(activeFilter),
        ...(filters.q ? { q: filters.q } : {}),
        ...(filters.vertical ? { vertical: filters.vertical } : {}),
        ...(filters.country ? { country: filters.country } : {}),
        ...(filters.source ? { source: filters.source } : {}),
        // Spread AFTER filterParams so the chosen ordering overrides a filter's
        // default. Every column — incl. heat — is now a real server-side sort,
        // so the default heat order spans every page (not just the visible one).
        // The ONE exception: a date window (Today, This week, …) keeps its own newest-added order
        // UNTIL the operator picks a non-default ordering, so landing on Today
        // still shows "leads added today" newest-first rather than by heat.
        // ("Closing soon" is agent-enforced — it ignores `sort` regardless.)
        ...(activeFilter?.window && sameSort(filters.sort, DEFAULT_LEAD_SORT)
          ? {}
          : leadSortParams(filters.sort)),
      };
      return salesApi.listLeads(params);
    },
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
  });

  // Per-filter lead counts shown beside each option label ("All (75)").
  // One lightweight `per_page: 1` query per filter (we only read `total`), keyed
  // off the active filters so the counts track the current search/vertical/
  // country narrowing — not the page. Cached + refreshed on the same cadence
  // as the list.
  const countQueries = useQueries({
    queries: STATUS_FILTERS.map((t) => {
      const params: ListLeadsParams = {
        per_page: 1,
        ...filterParams(t),
        ...(filters.q ? { q: filters.q } : {}),
        ...(filters.vertical ? { vertical: filters.vertical } : {}),
        ...(filters.country ? { country: filters.country } : {}),
        ...(filters.source ? { source: filters.source } : {}),
      };
      return {
        queryKey: ["sales", "leads", "count", t.key, filters],
        queryFn: () => salesApi.listLeads(params),
        refetchInterval: 60_000,
        placeholderData: (prev: SalesLeadsListPayload | undefined) => prev,
      };
    }),
  });
  const counts: Record<string, number | undefined> = {};
  STATUS_FILTERS.forEach((t, i) => {
    counts[t.key] = countQueries[i]?.data?.total;
  });

  const totalPages = leads.data
    ? Math.max(1, Math.ceil(leads.data.total / leads.data.per_page))
    : 1;
  const hasPrev = page > 1;
  const hasNext = leads.data ? page < totalPages : false;

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <StatusFilter
        value={tab}
        counts={counts}
        onChange={(k) => {
          setTab(k);
          setPage(1);
        }}
      />

      <LeadFilters
        q={filters.q}
        vertical={filters.vertical}
        country={filters.country}
        source={filters.source}
        sort={filters.sort}
        onChange={setFiltersAndResetPage}
      />

      {leads.isLoading && <PageLoader />}

      {leads.isError && <SalesErrorState error={leads.error} surface="leads" />}

      {leads.data && leads.data.items.length === 0 && (
        activeFilter?.closing ? (
          <SalesEmptyState
            variant="info"
            title="Nothing closing soon"
            description={`No live lead has a dated window in the next ${URGENCY_SUPER_HOT_DAYS} days. When one does, it'll appear here — and at the top of every list.`}
          />
        ) : activeFilter?.window === "today" ? (
          <SalesEmptyState
            variant="info"
            title="No leads added today yet"
            description="The agent is out hunting — new finds since its daily reset land here the moment they're grounded. Earlier leads live on the All filter; watch it work at the bottom of the Status tab."
          />
        ) : activeFilter?.window ? (
          <SalesEmptyState
            variant="info"
            title={`No leads added ${activeFilter.label.toLowerCase()}`}
            description="Nothing the agents found falls in this window. A day here starts at 09:00 Riga time, the same as the Today filter."
          />
        ) : (
          <SalesEmptyState
            variant="info"
            title="No leads match these filters"
            description="Drop the filters or check back in a moment — the autonomous loop is constantly producing new orgs."
          />
        )
      )}

      {leads.data && leads.data.items.length > 0 && (
        <>
          {/* Controlled sort: a column-header click bubbles up here, sets the
              server-side `sort` (ordering the WHOLE dataset, not just this
              page), and resets to page 1 — see `setFiltersAndResetPage`. */}
          <LeadsTable
            items={leads.data.items}
            editable
            sort={filters.sort}
            onSortChange={(sort) => setFiltersAndResetPage({ sort })}
          />
          <LeadsPagination
            total={leads.data.total}
            page={page}
            perPage={leads.data.per_page}
            totalPages={totalPages}
            hasPrev={hasPrev}
            hasNext={hasNext}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => p + 1)}
            onJump={setPage}
          />
        </>
      )}
    </Box>
  );
}
